import { ATTR_NAME } from 'constants/attr-name'
import { CLASS_NAME } from 'constants/class-names'
import { EventType, KeyType } from 'constants/enums'
import { Nullable } from 'constants/types'
import { ComponentBase } from 'utils/abstract-classes/component-base'
import { setStyles } from 'utils/dom'
import { hasTouch, isMobile } from 'utils/system/device'
import { HeaderStore } from 'components/header/store'

const {
    header: { menu: menuClass }
} = CLASS_NAME

/**
 * Arrow key to index for mapping key action to direction
 * ⬆️ is 0, and clockwise matching with CSS margin/padding property
 */
export enum ArrowDirection {
    ArrowUp = 0,
    Up = 0,
    ArrowRight = 1,
    Right = 1,
    ArrowDown = 2,
    Down = 2,
    // eslint-disable-next-line no-magic-numbers
    ArrowLeft = 3,
    // eslint-disable-next-line no-magic-numbers
    Left = 3
}

/**
 * Menu Item
 * the root element is li.top-menu__item
 */
export class MenuItem extends ComponentBase {
    /**
     * childrenWrapper: <ul> element in the component
     * children: <li> elements of it
     */
    public childrenWrapper: Nullable<HTMLUListElement>
    private children: HTMLLIElement[]
    /**
     * Linked list for root (L1) items
     * Used for resetting styles of L1 items
     */
    private _nextRootItem?: MenuItem
    /**
     * Linked list for prev/ next items
     * It starts from the first L1 to the last menu item
     * Used for Tab and Shift + Tab action
     */
    private _prevElement?: MenuItem
    private _nextElement?: MenuItem
    /**
     * Linked list for keyboard action
     * It matches with `ArrowDirection` values (⬆️ is 0)
     */
    private _arrowElement: { [key: number]: MenuItem } = {}
    /**
     * <a> element in the component
     */
    private anchor: HTMLAnchorElement

    /**
     * Initialize elements and set event listeners
     * @param {HTMLLIElement} element li.top-menu__item element of the menu item
     * @param {number} level The depth of the menu
     */
    public constructor(
        private readonly element: HTMLLIElement,
        private readonly level: number
    ) {
        super()

        if (!this.initElements()) {
            return
        }
        this.initEventListeners()
    }

    /**
     * Initialize elements. <a> is mandatory
     */
    private initElements(): boolean {
        const anchor = this.getElement<HTMLAnchorElement>('a', this.element)
        if (!anchor) {
            return false
        }
        this.anchor = anchor

        if (this.level === 1) {
            this.childrenWrapper = this.getElement<HTMLUListElement>(
                'ul',
                this.element
            )
            this.children = this.getElements<HTMLLIElement>(
                `.${menuClass.item}[${ATTR_NAME.header.menu.level}="2"]`,
                this.childrenWrapper
            )
        }
        return true
    }

    /**
     * Event listeners
     * Keyboard actions and mouse over
     */
    private initEventListeners(): void {
        // Keyboard actions
        this.anchor.addEventListener(EventType.KEYDOWN, (e: KeyboardEvent) =>
            this.handleKeyEvents(e)
        )

        // Mouse hover should steal the focus
        this.anchor.addEventListener(EventType.MOUSE_OVER, (e: MouseEvent) => {
            e.preventDefault()
            this.focus()
        })

        // Tablet: first click opens the sub-menus
        if (
            this.level === 1 &&
            this.children.length &&
            hasTouch() &&
            !isMobile()
        ) {
            this.anchor.addEventListener(
                EventType.TOUCH_START,
                (e: TouchEvent) => {
                    this.handleTouch(e)
                }
            )
        }
    }

    /**
     * Element setters: prev
     * @param {MenuItem} element
     */
    public set prevElement(element: MenuItem) {
        this._prevElement = element
    }

    /**
     * Element setters: next
     * @param {MenuItem} element
     */
    public set nextElement(element: MenuItem) {
        this._nextElement = element
    }

    /**
     * Element setters for keyboard actions
     * @param {MenuItem} element
     * @param {ArrowDirection} direction
     */
    public setArrowElement(element: MenuItem, direction: ArrowDirection): void {
        this._arrowElement[direction] = element
    }

    /**
     * Element setters: next root item chain for removing attributes
     * @param {MenuItem} element
     */
    public set nextRootItem(element: MenuItem) {
        this._nextRootItem = element
    }

    /**
     * Remove styles and execute same method of the next root item
     */
    public removeRootHTMLClasses(): void {
        this.element.classList.remove(
            menuClass.active,
            menuClass.noColumn,
            menuClass.oneColumn,
            menuClass.twoColumn
        )
        if (this.childrenWrapper && !isMobile()) {
            setStyles(this.childrenWrapper, { display: 'none' })
        }

        if (this._nextRootItem) {
            this._nextRootItem.removeRootHTMLClasses()
        }
    }

    /**
     * Handle when keyboard is used on the menu items
     * Escape: should close the menu
     * Tab and shift tab: navigate to next and prev element
     * Arrow keys should let you move through the menu and sub menu items faster
     *
     * @param {KeyboardEvent} e
     */
    private handleKeyEvents(e: KeyboardEvent): void {
        const navigationEvents = [
            KeyType.DOWN,
            KeyType.DOWN_IE,
            KeyType.UP,
            KeyType.UP_IE,
            KeyType.RIGHT,
            KeyType.RIGHT_IE,
            KeyType.LEFT,
            KeyType.LEFT_IE,
            KeyType.TAB
        ] as string[]

        if (e.key === KeyType.ESC || e.key === KeyType.ESC_IE) {
            HeaderStore.getInstance().menu.closeMenu()
            return
        }

        if (isMobile() || !navigationEvents.includes(e.key)) {
            return
        }

        switch (e.key) {
            case KeyType.DOWN:
            case KeyType.DOWN_IE:
            case KeyType.UP:
            case KeyType.UP_IE:
            case KeyType.RIGHT:
            case KeyType.RIGHT_IE:
            case KeyType.LEFT:
            case KeyType.LEFT_IE:
                // Arrow keys
                if (this._arrowElement[ArrowDirection[e.key]]) {
                    e.preventDefault()
                    this._arrowElement[ArrowDirection[e.key]].focus()
                }
                return
            case KeyType.TAB:
                if (!e.shiftKey && this._nextElement) {
                    // Tab: next element
                    e.preventDefault()
                    this._nextElement.focus()
                } else if (e.shiftKey && this._prevElement) {
                    // Shift + Tab: prev element
                    if (this._prevElement) {
                        e.preventDefault()
                        this._prevElement.focus()
                    }
                } else if (!e.shiftKey && !this._nextElement) {
                    // Tab on the last element: back to hamburger
                    e.preventDefault()
                    HeaderStore.getInstance().menu.closeMenu()
                    HeaderStore.getInstance().menu.hamburger.focus()
                } else if (e.shiftKey && !this._prevElement) {
                    // Shift + Tab on the first element: back to hamburger
                    e.preventDefault()
                    HeaderStore.getInstance().menu.closeMenu()
                    HeaderStore.getInstance().menu.hamburger.focus()
                }
                return
            default:
                return
        }
    }

    /**
     * Focus action
     */
    public focus(): void {
        HeaderStore.getInstance().menu.removeRootItemClasses()

        if (this._arrowElement[ArrowDirection.Left] && !isMobile()) {
            this._arrowElement[ArrowDirection.Left].addActiveClass()
            if (this._arrowElement[ArrowDirection.Left].childrenWrapper) {
                setStyles(
                    this._arrowElement[ArrowDirection.Left].childrenWrapper,
                    {}
                )
            }
            this._arrowElement[ArrowDirection.Left].setLevel2Width()
        }

        this.anchor.focus()
    }

    public addActiveClass(): void {
        this.element.classList.add(menuClass.active)
    }

    /**
     * Adjust the width of L2 and menu depending on number of columns
     * only applies to sm breakpoint up design
     */
    public setLevel2Width(): void {
        if (isMobile()) {
            return
        }

        const firstElement = this.children[0]
        const lastElement = this.children[this.children.length - 1]

        if (firstElement && lastElement && this.childrenWrapper) {
            const firstPosX = firstElement.getClientRects()[0].x
            const lastPosX = lastElement.getClientRects()[0].x
            if (firstPosX !== lastPosX) {
                // if first element x position is different than last element x position
                // => we have 2 columns layout
                HeaderStore.getInstance().menu.setMenuClass(menuClass.twoColumn)
                this.childrenWrapper.classList.add(menuClass.twoColumn)
                return
            }

            // if first element x position is same with last element x position
            // => we have 1 columns layout
            HeaderStore.getInstance().menu.setMenuClass(menuClass.oneColumn)
            this.childrenWrapper.classList.add(menuClass.oneColumn)
            return
        }

        // No L2 items here
        HeaderStore.getInstance().menu.setMenuClass(menuClass.noColumn)
    }

    /**
     * Trace the last touched item
     * On Tablet or touchable desktop, the first touch should open the sub-menu
     *
     * @param {TouchEvent} e
     */
    private handleTouch(e: TouchEvent): void {
        // This is the second touch, so open the anchor
        if (HeaderStore.getInstance().lastTouchedL1Item === this) {
            return
        }

        // This is the first touch
        e.preventDefault()
        HeaderStore.getInstance().lastTouchedL1Item = this
        this.focus()
    }
}
